import React, { useMemo } from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Numeric } from '@swyftx/aviary/atoms/Typography';
import { MarginHistoricOrder } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstrumentDetails, useMarginInstruments } from 'src/lib/margin/hooks/instruments';

type Props = {
  order?: MarginHistoricOrder;
  showSubtitle?: boolean;
};

const MarginHistoryTableLotsProfitLoss: React.FC<Props> = ({ order, showSubtitle = true }) => {
  const { getMarketById } = useMarginInstruments();
  const { symbol } = useMarginInstrumentDetails({ instrument: getMarketById(order?.marketUuid) });
  const { currency } = useMarginAccount();

  const title = useMemo(() => {
    const volume = order?.filledVolume || order?.volume;

    if (!volume || Big(volume).eq(0))
      return (
        <Numeric weight='bold' color='primary'>
          -
        </Numeric>
      );

    return (
      <Numeric weight='bold' color='primary'>
        {formatCurrency(volume)} {symbol}
      </Numeric>
    );
  }, [order, symbol]);

  const subTitle = useMemo(() => {
    if (!order) return null;

    if (order.closePnl) {
      return (
        <Numeric color={Big(order.closePnl).lt(0) ? 'error' : 'success'}>
          {formatCurrency(order.closePnl, currency, { appendCode: true, hideCode: false })}
        </Numeric>
      );
    }

    const value = order.fillPrice || order.price || order.stopLoss || order.takeProfit;

    if (!value || Big(value).eq(0)) {
      return <Numeric color='secondary'>-</Numeric>;
    }

    return <Numeric color='secondary'>@ {formatCurrency(value)}</Numeric>;
  }, [currency, order]);

  if (!order) return null;

  return (
    <FlexLayout justifyContent='start' direction='column' alignItems='end'>
      {title}
      {showSubtitle && subTitle}
    </FlexLayout>
  );
};

export { MarginHistoryTableLotsProfitLoss };
